import { z } from 'zod';
import { IpAddress, IsoTime, ServerId } from './basics.js';
import { ApplyComplianceFixesArgs, CommandStatus } from './command.js';
import { ServerGroupId, ServerStatus } from './server.js';
/**
 * Actions (knowledge doc section 12): one-time things a person asks for — ban this
 * address now — as opposed to settings, which are the desired state.
 *
 * An action names a target (one server, a group, or every server) and the API fans
 * it out into **one command per server**, each with an expiry, so a server that was
 * offline never runs an old ban hours later (rule 8). The agent picks its commands
 * up on the same long poll that carries its settings, runs each through the root
 * helper, and reports back; the dashboard shows the result per server.
 *
 * Only the actions that exist are listed. Section 12's risk table names more — rescan
 * files, restart CrowdSec, accept a file change — and each arrives with the part
 * that builds the helper operation behind it (P7.3 for the Wazuh ones).
 */
export const ActionType = z.enum([
    'ban_ip',
    'unban_ip',
    'upgrade_crowdsec_hub',
    'apply_compliance_fixes',
]);
/** Who an action is for. `all` is every server that is not revoked. */
export const ActionTarget = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('server'), id: ServerId }).strict(),
    z.object({ kind: z.literal('group'), id: ServerGroupId }).strict(),
    z.object({ kind: z.literal('all') }).strict(),
]);
/** Section 12: "Default ban duration (for example 4 h) and a maximum (for example 30 days)". */
export const BAN_DEFAULT_SECONDS = 4 * 3600;
export const BAN_MAX_SECONDS = 30 * 24 * 3600;
export const BAN_MIN_SECONDS = 60;
/**
 * Section 12's example is 15 minutes for bans: long enough for an agent parked on
 * its long poll to pick a command up, short enough that one which was offline does
 * not act on a decision somebody made an hour ago.
 */
export const COMMAND_TTL_SECONDS = 15 * 60;
/**
 * Compliance fixes may run for ten minutes after the agent collects them, and the
 * API's sweep expires a command that is still `sent` when this is up, so they get
 * twice the usual. Applying them late is harmless — each is idempotent and an admin
 * approved this very list — which is what the short expiry on a ban protects against.
 */
export const COMPLIANCE_COMMAND_TTL_SECONDS = 30 * 60;
/**
 * What a person types. The API turns `reason` into CrowdSec's
 * `bartizo: <email>: <reason>`, so the decision says who asked from the dashboard
 * and `cscli decisions list` on the server can tell it from an automatic one.
 */
export const BanRequest = z.object({
    ip: IpAddress,
    duration_seconds: z
        .number()
        .int()
        .min(BAN_MIN_SECONDS)
        .max(BAN_MAX_SECONDS)
        .default(BAN_DEFAULT_SECONDS),
    reason: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[^\p{Cc}]+$/u, 'must not contain control characters'),
    /**
     * Section 12 refuses private ranges "unless explicitly allowed". Admin only: a
     * private address is usually a load balancer or a colleague, and banning one is
     * how a site goes down from the inside.
     */
    allow_private: z.boolean().default(false),
    /**
     * P11.9.3 (C6): `captcha` asks a visitor to prove they are a person rather than
     * refusing them — only where the server's bouncer can show a challenge; other
     * servers are skipped (`no_challenge`).
     */
    remediation: z.enum(['ban', 'captcha']).default('ban'),
});
export const UnbanRequest = z.object({ ip: IpAddress });
export const CreateActionRequest = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ban_ip'), target: ActionTarget, args: BanRequest }),
    z.object({ type: z.literal('unban_ip'), target: ActionTarget, args: UnbanRequest }),
    /** P11.9.2: the hub's items upgraded where a newer version exists; tainted ones are kept. */
    z.object({
        type: z.literal('upgrade_crowdsec_hub'),
        target: ActionTarget,
        args: z.object({}).strict().default({}),
    }),
    /**
     * P11.19: the catalogue's fixes for failing compliance checks, named by id. High
     * risk (section 12): it changes a server's configuration and removes packages, so
     * an admin asks for it and re-enters their credentials first.
     */
    z.object({
        type: z.literal('apply_compliance_fixes'),
        target: ActionTarget,
        args: ApplyComplianceFixesArgs,
    }),
]);
/** Why a server in the target will not be sent the command. */
export const SkipReason = z.enum([
    /** Revoked servers are out of the fleet. */
    'revoked',
    /** Awaiting approval: it may heartbeat and nothing else. */
    'pending',
    /** Its heartbeat does not report CrowdSec, so there is nothing to ban with. */
    'no_crowdsec',
    /**
     * The address is one of this server's own proxies, as its web environment
     * detection found them (rule 16; P5.5). Skipped on that server rather than
     * refused for everyone: the list is the agent's word, and a compromised agent
     * must not be able to make an address unbannable across the fleet — only on
     * itself, which it could do anyway by not running the command.
     */
    'proxy_address',
    /** A challenge was asked for, and this server's web layer cannot show one (P11.9.3). */
    'no_challenge',
]);
/** One server as the preview and the result show it. */
export const ActionServer = z.object({
    serverId: ServerId,
    name: z.string().min(1).max(255),
    serverStatus: ServerStatus,
});
/**
 * Section 12: "Bulk actions show a preview first". What would happen, per server,
 * and what the person should know before confirming — no row is written.
 */
export const ActionPreview = z.object({
    type: ActionType,
    target: ActionTarget,
    /** Servers that would be sent a command. An offline one keeps it until it expires. */
    servers: z.array(ActionServer).max(5000),
    skipped: z.array(ActionServer.extend({ reason: SkipReason })).max(5000),
    /** Section 12's unban warnings, and anything else worth a second look. */
    warnings: z.array(z.string().max(500)).max(20),
});
/** One server's command, as the dashboard reads the outcome. */
export const ActionServerResult = ActionServer.extend({
    commandId: z.uuid(),
    status: CommandStatus,
    /** What the server said, or why it was skipped. Bounded, like everything an agent sends. */
    detail: z.string().max(1000).nullable(),
    completedAt: IsoTime.nullable(),
});
export const ActionSource = z.enum(['manual', 'ai', 'policy']);
export const ActionSummary = z.object({
    id: z.uuid(),
    type: ActionType,
    target: ActionTarget,
    /** The request as it was accepted, with the address and the duration. */
    args: z.record(z.string(), z.unknown()),
    source: ActionSource,
    requestedByEmail: z.email().nullable(),
    createdAt: IsoTime,
    expiresAt: IsoTime,
    stoppedAt: IsoTime.nullable(),
    /** How many commands are in each state, for a list that does not load every row. */
    counts: z.record(CommandStatus, z.number().int().nonnegative()),
});
export const ActionDetail = ActionSummary.extend({
    results: z.array(ActionServerResult).max(5000),
    warnings: z.array(z.string().max(500)).max(20),
});
export const ACTION_PAGE_DEFAULT = 50;
export const ACTION_PAGE_MAX = 200;
export const ActionListQuery = z.object({
    limit: z.coerce.number().int().min(1).max(ACTION_PAGE_MAX).default(ACTION_PAGE_DEFAULT),
    cursor: z.string().max(512).optional(),
    server_id: ServerId.optional(),
    /** Only actions about this address (P11.1's address page). */
    ip: IpAddress.optional(),
});
/** `POST /actions/:id/stop`: how many commands no agent had collected yet were cancelled. */
export const StopActionResponse = z.object({
    id: z.uuid(),
    cancelled: z.number().int().nonnegative(),
});
//# sourceMappingURL=action.js.map